/**
 * fileDrop — helpers for "drop a PDF anywhere in this window" on desktop.
 *
 * Until now nothing in the desktop shell handled `drop` at all: the home
 * page's dashed dropzone was a styled <div> and no document-level listener
 * existed, so Chromium tried to navigate to the dropped file:// URL, the
 * main process's will-navigate guard blocked it, and the drop did nothing.
 */

/** First PDF in a DataTransfer's file list, or null. Extension wins over MIME
 *  because some Windows installs report an empty type for .pdf. */
export function pickDroppedPdf(dataTransfer) {
  const files = dataTransfer?.files;
  if (!files || files.length === 0) return null;
  for (const f of files) {
    if (!f) continue;
    const name = String(f.name || '');
    if (/\.pdf$/i.test(name) || f.type === 'application/pdf') return f;
  }
  return null;
}

/** Hex SHA-256 of an ArrayBuffer (WebCrypto). */
export async function sha256Hex(arrayBuffer) {
  const digest = await crypto.subtle.digest('SHA-256', arrayBuffer);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Turn a dropped File into the { file, filePath } the viewer route expects.
 * On desktop, asks the main process to register the path (Recent Files +
 * save-in-place) after proving possession of the bytes; on failure the file
 * still opens — just without a path (Save prompts for a location).
 */
export async function prepareDroppedPdf(file, electronAPI = (typeof window !== 'undefined' ? window.electronAPI : undefined)) {
  const buffer = await file.arrayBuffer();
  const pdfFile = new File([buffer], file.name, { type: 'application/pdf' });
  let filePath;
  if (electronAPI?.getPathForFile && electronAPI?.registerDroppedFile) {
    try {
      const candidate = electronAPI.getPathForFile(file);
      if (candidate) {
        const r = await electronAPI.registerDroppedFile(candidate, await sha256Hex(buffer));
        if (r?.ok) filePath = r.path;
      }
    } catch {
      // non-fatal: open without a path
    }
  }
  return { file: pdfFile, filePath };
}
